/**
 * 新規登録ページ - melta UI
 * Step1: アカウント情報入力（メール・パスワード・氏名）
 * Step2: 利用形態選択（個人 or 組織）
 * Step3: 確認・登録完了
 */

import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { signUp, confirmSignUp } from '../services/auth';
import { Icon } from '../components/icons';
import { Button, Card, Input, Alert } from '../components/ui';

const STEPS = {
  ACCOUNT: 1,
  ORG_TYPE: 2,
  VERIFY: 3,
  COMPLETE: 4,
};

export default function Register() {
  const navigate = useNavigate();
  const [step, setStep] = useState(STEPS.ACCOUNT);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  // フォーム値
  const [form, setForm] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    name: '',
    orgType: '', // 'personal' or 'organization'
    orgName: '',
    verificationCode: '',
  });

  const updateForm = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    setError('');
  };

  // パスワードポリシーチェック
  const passwordChecks = {
    length: form.password.length >= 8,
    uppercase: /[A-Z]/.test(form.password),
    lowercase: /[a-z]/.test(form.password),
    number: /[0-9]/.test(form.password),
    match: form.password === form.confirmPassword && form.confirmPassword.length > 0,
  };
  const isPasswordValid = Object.values(passwordChecks).every(Boolean);

  // Step1: アカウント情報バリデーション
  const validateAccount = () => {
    if (!form.email || !form.password || !form.name) {
      setError('すべての項目を入力してください');
      return false;
    }
    if (!isPasswordValid) {
      setError('パスワードの要件を満たしてください');
      return false;
    }
    return true;
  };

  // フォーム全体が有効かチェック
  const isAccountFormValid = form.email && form.name && isPasswordValid;

  // Step1 → Step2
  const handleAccountNext = (e) => {
    e.preventDefault();
    if (validateAccount()) {
      setStep(STEPS.ORG_TYPE);
    }
  };

  // Step2: 利用形態選択 → Cognito signUp
  const handleOrgTypeNext = async (e) => {
    e.preventDefault();
    if (!form.orgType) {
      setError('利用形態を選択してください');
      return;
    }
    if (form.orgType === 'organization' && !form.orgName.trim()) {
      setError('組織名を入力してください');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      // Cognito signUp
      await signUp({
        email: form.email,
        password: form.password,
        name: form.name,
        orgType: form.orgType,
      });

      setStep(STEPS.VERIFY);
    } catch (err) {
      if (err.code === 'UsernameExistsException') {
        setError('このメールアドレスはすでに登録されています');
      } else {
        setError(err.message || '登録に失敗しました');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Step3: メール確認コード入力
  const handleVerify = async (e) => {
    e.preventDefault();
    if (!form.verificationCode) {
      setError('確認コードを入力してください');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      // Cognito メール確認のみ（DynamoDB登録はログイン後）
      await confirmSignUp(form.email, form.verificationCode);

      // 登録情報をsessionStorageに保存してログイン後に使用
      sessionStorage.setItem('pendingRegistration', JSON.stringify({
        name: form.name,
        orgType: form.orgType,
        orgName: form.orgType === 'organization' ? form.orgName : form.name,
      }));

      setStep(STEPS.COMPLETE);
    } catch (err) {
      if (err.code === 'CodeMismatchException') {
        setError('確認コードが正しくありません');
      } else if (err.code === 'ExpiredCodeException') {
        setError('確認コードの有効期限が切れています。再送信してください');
      } else {
        setError(err.message || '確認に失敗しました');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // 確認コード再送信
  const handleResendCode = async () => {
    setIsLoading(true);
    setError('');
    try {
      const { resendConfirmationCode } = await import('../services/auth');
      await resendConfirmationCode(form.email);
      setError(''); // クリア
      alert('確認コードを再送信しました');
    } catch (err) {
      setError('再送信に失敗しました');
    } finally {
      setIsLoading(false);
    }
  };

  // ステップインジケーター
  const StepIndicator = () => (
    <div className="flex items-center justify-center gap-2 mb-8">
      {['アカウント情報', '利用形態', 'メール確認'].map((label, i) => (
        <React.Fragment key={i}>
          <div className="flex flex-col items-center gap-1">
            <div
              className={`
                w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium
                ${step > i + 1
                  ? 'bg-primary-500 text-white'
                  : step === i + 1
                  ? 'border-2 border-primary-500 text-primary-500'
                  : 'bg-slate-100 text-slate-500'
                }
              `}
            >
              {step > i + 1 ? <Icon name="check" className="w-4 h-4" /> : i + 1}
            </div>
            <span className={`text-xs ${step === i + 1 ? 'text-primary-500 font-medium' : 'text-slate-500'}`}>
              {label}
            </span>
          </div>
          {i < 2 && (
            <div className={`flex-1 h-0.5 mx-2 ${step > i + 1 ? 'bg-primary-500' : 'bg-slate-200'}`} />
          )}
        </React.Fragment>
      ))}
    </div>
  );

  // Step1: アカウント情報
  if (step === STEPS.ACCOUNT) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-primary-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Icon name="wifi" className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900">新規登録</h1>
            <p className="text-sm text-slate-500 mt-1">drone-ops にようこそ</p>
          </div>
          <StepIndicator />

          <form onSubmit={handleAccountNext} className="space-y-4">
            {error && <Alert variant="error">{error}</Alert>}

            <Input
              label="氏名"
              type="text"
              value={form.name}
              onChange={(e) => updateForm('name', e.target.value)}
              placeholder="例：岡崎 年弘"
              required
              autoFocus
              icon={<Icon name="user" className="w-5 h-5" />}
            />

            <Input
              label="メールアドレス"
              type="email"
              value={form.email}
              onChange={(e) => updateForm('email', e.target.value)}
              placeholder="example@nttedt.co.jp"
              required
              icon={<Icon name="mail" className="w-5 h-5" />}
            />

            <Input
              label="パスワード"
              type="password"
              value={form.password}
              onChange={(e) => updateForm('password', e.target.value)}
              placeholder="8文字以上、大小英字・数字含む"
              required
              icon={<Icon name="lock" className="w-5 h-5" />}
            />

            <Input
              label="パスワード確認"
              type="password"
              value={form.confirmPassword}
              onChange={(e) => updateForm('confirmPassword', e.target.value)}
              placeholder="パスワード再入力"
              required
              icon={<Icon name="lock" className="w-5 h-5" />}
            />

            {/* パスワード要件チェックリスト */}
            {form.password && (
              <div className="bg-slate-50 rounded-lg p-3 space-y-1.5">
                <p className="text-xs font-medium text-slate-600 mb-2">パスワード要件:</p>
                <div className={`flex items-center gap-2 text-xs ${passwordChecks.length ? 'text-emerald-600' : 'text-slate-400'}`}>
                  <Icon name={passwordChecks.length ? 'checkCircle' : 'circle'} className="w-3.5 h-3.5" />
                  8文字以上
                </div>
                <div className={`flex items-center gap-2 text-xs ${passwordChecks.uppercase ? 'text-emerald-600' : 'text-slate-400'}`}>
                  <Icon name={passwordChecks.uppercase ? 'checkCircle' : 'circle'} className="w-3.5 h-3.5" />
                  大文字を含む (A-Z)
                </div>
                <div className={`flex items-center gap-2 text-xs ${passwordChecks.lowercase ? 'text-emerald-600' : 'text-slate-400'}`}>
                  <Icon name={passwordChecks.lowercase ? 'checkCircle' : 'circle'} className="w-3.5 h-3.5" />
                  小文字を含む (a-z)
                </div>
                <div className={`flex items-center gap-2 text-xs ${passwordChecks.number ? 'text-emerald-600' : 'text-slate-400'}`}>
                  <Icon name={passwordChecks.number ? 'checkCircle' : 'circle'} className="w-3.5 h-3.5" />
                  数字を含む (0-9)
                </div>
                <div className={`flex items-center gap-2 text-xs ${passwordChecks.match ? 'text-emerald-600' : 'text-slate-400'}`}>
                  <Icon name={passwordChecks.match ? 'checkCircle' : 'circle'} className="w-3.5 h-3.5" />
                  パスワードが一致
                </div>
              </div>
            )}

            <Button
              type="submit"
              className="w-full"
              size="lg"
              disabled={!isAccountFormValid}
            >
              次へ
              <Icon name="chevronRight" className="w-4 h-4" />
            </Button>
          </form>

          <div className="mt-6 text-center text-sm text-slate-500">
            すでにアカウントをお持ちの方は{' '}
            <Link to="/login" className="text-primary-500 hover:text-primary-700 font-medium">
              ログイン
            </Link>
          </div>
        </Card>
      </div>
    );
  }

  // Step2: 利用形態選択
  if (step === STEPS.ORG_TYPE) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <div className="text-center mb-6">
            <h1 className="text-2xl font-bold text-slate-900">利用形態を選択</h1>
            <p className="text-sm text-slate-500 mt-1">後から変更することもできます</p>
          </div>
          <StepIndicator />

          <form onSubmit={handleOrgTypeNext} className="space-y-4">
            {error && <Alert variant="error">{error}</Alert>}

            <div className="space-y-3">
              {/* 個人利用 */}
              <div
                className={`
                  flex items-start gap-4 p-4 rounded-xl border-2 cursor-pointer transition-colors
                  ${form.orgType === 'personal'
                    ? 'border-primary-500 bg-primary-50'
                    : 'border-slate-200 hover:border-slate-300'
                  }
                `}
                onClick={() => updateForm('orgType', 'personal')}
              >
                <div className="w-12 h-12 bg-slate-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Icon name="user" className="w-6 h-6 text-slate-600" />
                </div>
                <div className="flex-1">
                  <div className="font-medium text-slate-900">個人として使う</div>
                  <div className="text-sm text-slate-500 mt-1">
                    自分だけでドローンを管理します。後から組織にアップグレードできます。
                  </div>
                </div>
                <div className={`
                  w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-1
                  ${form.orgType === 'personal' ? 'border-primary-500 bg-primary-500' : 'border-slate-300'}
                `}>
                  {form.orgType === 'personal' && (
                    <div className="w-2 h-2 rounded-full bg-white" />
                  )}
                </div>
              </div>

              {/* 組織利用 */}
              <div
                className={`
                  flex items-start gap-4 p-4 rounded-xl border-2 cursor-pointer transition-colors
                  ${form.orgType === 'organization'
                    ? 'border-primary-500 bg-primary-50'
                    : 'border-slate-200 hover:border-slate-300'
                  }
                `}
                onClick={() => updateForm('orgType', 'organization')}
              >
                <div className="w-12 h-12 bg-slate-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Icon name="building" className="w-6 h-6 text-slate-600" />
                </div>
                <div className="flex-1">
                  <div className="font-medium text-slate-900">組織として使う</div>
                  <div className="text-sm text-slate-500 mt-1">
                    チームでドローンを管理します。メンバーを招待して共同管理できます。
                  </div>
                </div>
                <div className={`
                  w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-1
                  ${form.orgType === 'organization' ? 'border-primary-500 bg-primary-500' : 'border-slate-300'}
                `}>
                  {form.orgType === 'organization' && (
                    <div className="w-2 h-2 rounded-full bg-white" />
                  )}
                </div>
              </div>
            </div>

            {/* 組織名入力（組織選択時のみ表示） */}
            {form.orgType === 'organization' && (
              <Input
                label="組織名"
                type="text"
                value={form.orgName}
                onChange={(e) => updateForm('orgName', e.target.value)}
                placeholder="例：株式会社NTT e-Drone Technology"
                required
                autoFocus
                icon={<Icon name="building" className="w-5 h-5" />}
              />
            )}

            <div className="flex gap-3 pt-2">
              <Button
                type="button"
                variant="secondary"
                className="flex-1"
                onClick={() => setStep(STEPS.ACCOUNT)}
              >
                <Icon name="chevronLeft" className="w-4 h-4" />
                戻る
              </Button>
              <Button
                type="submit"
                className="flex-1"
                disabled={isLoading || !form.orgType}
                loading={isLoading}
              >
                登録する
              </Button>
            </div>
          </form>
        </Card>
      </div>
    );
  }

  // Step3: メール確認コード
  if (step === STEPS.VERIFY) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Icon name="mail" className="w-8 h-8 text-primary-500" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900">メールを確認してください</h1>
            <p className="text-sm text-slate-500 mt-2">
              <span className="font-medium text-slate-700">{form.email}</span> に確認コードを送信しました
            </p>
          </div>
          <StepIndicator />

          <form onSubmit={handleVerify} className="space-y-4">
            {error && <Alert variant="error">{error}</Alert>}

            <Input
              label="確認コード"
              type="text"
              value={form.verificationCode}
              onChange={(e) => updateForm('verificationCode', e.target.value)}
              placeholder="6桁のコードを入力"
              maxLength={6}
              required
              autoFocus
              className="text-center text-2xl tracking-widest"
            />

            <Button type="submit" className="w-full" size="lg" loading={isLoading}>
              確認する
            </Button>

            <div className="text-center text-sm text-slate-500">
              コードが届かない場合は{' '}
              <button
                type="button"
                className="text-primary-500 hover:text-primary-700 font-medium"
                onClick={handleResendCode}
              >
                再送信
              </button>
            </div>
          </form>
        </Card>
      </div>
    );
  }

  // Step4: 登録完了
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md text-center">
        <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <Icon name="checkCircle" className="w-10 h-10 text-emerald-500" />
        </div>
        <h1 className="text-2xl font-bold text-slate-900 mb-2">登録完了！</h1>
        <p className="text-slate-500 mb-6">
          {form.orgType === 'organization'
            ? `「${form.orgName}」として登録されました`
            : '個人アカウントが作成されました'}
        </p>

        <div className="bg-slate-50 rounded-xl p-4 mb-6">
          <p className="text-slate-700">ログインしてドローン管理を始めましょう。</p>
          {form.orgType === 'personal' && (
            <p className="text-sm text-slate-500 mt-2 flex items-center justify-center gap-1">
              <Icon name="info" className="w-4 h-4" />
              設定画面からいつでも組織にアップグレードできます
            </p>
          )}
        </div>

        <Button className="w-full" size="lg" onClick={() => navigate('/login')}>
          ログインへ
        </Button>
      </Card>
    </div>
  );
}
